import { App } from '@capacitor/app'
import {
  ActionPerformed,
  PermissionStatus,
  PushNotifications,
  PushNotificationSchema,
  Token,
} from '@capacitor/push-notifications'
import { CreateNotificationInputProps, CreateNotificationProps } from 'types/NotificationTypes'
import { isPushNotification, isWeb } from './constants'
import { httpCodeAuth, processError } from './requests'
import { ResponseProps } from './types'
import { updateUser } from './users'

export const registerApp = async (onAppStateActive: () => Promise<void>) => {
  const handleAppStateChange = async (isActive: boolean) => {
    if (isActive) {
      await onAppStateActive()
    }
  }

  if (isWeb) {
    // Web: Handle visibility changes
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        handleAppStateChange(!document.hidden)
      })
      // Trigger on initial load to simulate active state
      handleAppStateChange(!document.hidden)
    }
  } else {
    // Mobile: Handle app state changes using Capacitor
    App.addListener('appStateChange', ({ isActive }) => {
      handleAppStateChange(isActive)
    })

    // Mobile: Handle app pause event using Capacitor
    App.addListener('pause', () => {
      // Exit chat room when app is unloaded to avoid loading messages and missing notifications
      const isChatRoom = /^\/messaging\/[a-zA-Z0-9_]+$/.test(window.location.pathname)
      if (isChatRoom) window.location.href = '/messaging'
    })

    // Mobile: Handle app pause event using Capacitor
    // App.addListener('resume', () => {})

    // Trigger on initial load to simulate active state
    const { isActive } = await App.getState()
    handleAppStateChange(isActive)
  }
}

export const registerNotifications = async (userId: string): Promise<void> => {
  try {
    if (!isPushNotification) return
    // Registration
    let permStatus: PermissionStatus = await PushNotifications.checkPermissions()
    if (permStatus.receive !== 'granted') permStatus = await PushNotifications.requestPermissions()
    if (permStatus.receive === 'granted') PushNotifications.register()
    // Listener to trap registration events
    PushNotifications.addListener('registrationError', (error: any) => console.error('Error registering:', error))
    PushNotifications.addListener('registration', async (token: Token) => {
      try {
        // Add the device to user profile
        await updateUser(userId, undefined, undefined, undefined, undefined, undefined, token.value)
      } catch (error) {
        console.error('Error registering notification device:', error)
        // Optional: Show a user-facing error message or retry mechanism
      }
    })

    // Listener to handle incoming notifications when the app is open
    PushNotifications.addListener('pushNotificationReceived', async (n: PushNotificationSchema) => {
      try {
        // Ignore silent validation messages
        if (n.data?.validation !== 'check') {
          // FIXME: Implement in-app toast or badge indicator  // for urgent notifications
          // FIXME: await PushNotifications.removeDeliveredNotifications() // for non-urgent notifications
        }
      } catch (error) {
        console.error('Error handling incoming notification when the app is open:', error)
        // Optional: Show a user-facing error message or retry mechanism
      }
    })

    // Listener to handle notification tap when the app is closed
    PushNotifications.addListener('pushNotificationActionPerformed', (a: ActionPerformed) => {
      try {
        // Ignore silent validation messages
        if (a.notification.data?.validation !== 'check' && a.notification.data?.url) {
          window.location.href = a.notification.data.url
        }
      } catch (error) {
        console.error('Error handling incoming notification when the app is closed:', error)
        // Optional: Show a user-facing error message or retry mechanism
      }
    })
  } catch (error) {
    console.error('Error initializing push notifications:', error)
  }
}

export const createNotification = async (
  notification: CreateNotificationProps,
  sendSms: boolean = false,
  sendPush: boolean = true,
): Promise<ResponseProps> => {
  try {
    const body: CreateNotificationInputProps = { ...notification, sendSms, sendPush, saveRecord: true }
    const { data } = await httpCodeAuth('post', `/api/notifications`, body as unknown as Record<string, unknown>, 30000)
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}
